import "./style.scss";

import { advanceSliceRowKey } from "components/AdvanceManagement/utils/advanceSliceFieldRules";
import AddTextButton from "components/shared/AddTextButton";
import RadioFormComponent from "components/shared/FormComponent/RadioFormComponent";
import { SmallBody } from "helpers/TypographyComponents";
import { formatNumber } from "helpers/utils";
import type {
    Control,
    FieldArrayWithId,
    FieldErrors,
    UseFormSetValue,
    UseFormTrigger,
    UseFormWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Binary } from "types";

import AdvanceSlicesTable from "../AdvanceSlicesTable";
import type { TAdvancePayment } from "../NewAdvanceModal";
import AdvanceFieldsLine from "../NewAdvanceModal/AdvanceFieldsLine";

export type AdvanceSlicesFormData = {
    control: Control<TAdvancePayment>;
    watch: UseFormWatch<TAdvancePayment>;
    errors: FieldErrors<TAdvancePayment>;
    setValue: UseFormSetValue<TAdvancePayment>;
    trigger: UseFormTrigger<TAdvancePayment>;
};

export interface IAdvanceSlicesFormBody {
    /**
     * React-hook-form utilities provided by the parent (useForm + useFieldArray).
     */
    formData: AdvanceSlicesFormData;
    /**
     * Slice fields from useFieldArray.
     */
    fields: FieldArrayWithId<TAdvancePayment, "slices", "id">[];
    /**
     * Watched slice values (useWatch), used to compute the total amount.
     */
    watchedSlices: TAdvancePayment["slices"];
    /**
     * Appends an empty slice row.
     */
    appendRow: () => void;
    /**
     * Removes the slice at the given index.
     */
    removeRow: (index: number) => void;
    /**
     * Whether the form is in update mode (existing slices).
     */
    isUpdate?: boolean;
    /**
     * Whether an advance payment already exists on this TPD (shows a warning message).
     */
    advanceExistsOnTPD?: boolean;
    /**
     * Payment type: "advance" or "package" (forfait).
     */
    type?: "advance" | "package";
    /**
     * Whether this is a package (1) or a standard advance payment (0).
     */
    isForfait?: Binary;
    /**
     * Enables compact mode (limited horizontal space): slice index and delete button are
     * absolutely positioned, and amount/date field widths are reduced to give more room
     * to the comment field. Used in the term form inline context.
     */
    compact?: boolean;
    /**
     * Read-only mode: all slice fields are disabled and add/delete controls are hidden.
     * Used in edit mode of the term form where advance payments cannot be modified here.
     */
    readOnly?: boolean;
    /**
     * Earliest selectable payment date (term form: title not published before the contract period).
     */
    minPaymentDate?: Date;
    /** Replaces the default total sentence. The amount is still appended. */
    totalLabel?: string;
}

/**
 * Shared presentational component that renders the advance payment slices form body.
 * Used by both NewAdvanceModal (inside a modal) and InlineAdvanceForm (inline in the term form).
 *
 * This component is purely presentational: it owns no state.
 * The useForm + useFieldArray logic stays in the parent component.
 */
const AdvanceSlicesFormBody = ({
    formData,
    fields,
    watchedSlices,
    appendRow,
    removeRow,
    isUpdate = false,
    advanceExistsOnTPD = false,
    type = "advance",
    isForfait = 0,
    compact = false,
    readOnly = false,
    minPaymentDate,
    totalLabel,
}: IAdvanceSlicesFormBody) => {
    const [t] = useTranslation();
    const { control, watch, errors, setValue, trigger } = formData;

    const total = watchedSlices?.reduce((acc, { amount }) => acc + +(amount ?? 0), 0);

    return (
        <div className="slices-group">
            {!isUpdate && advanceExistsOnTPD && !isForfait && (
                <p style={{ fontSize: "0.85rem", fontWeight: 500 }}>{t("BalanceUpdate.AdvancePage.AdvanceExists")}</p>
            )}

            <div className="slices-group">
                {compact ? (
                    <div className="slices-container">
                        {fields.map((slice, index) => (
                            <AdvanceFieldsLine
                                key={advanceSliceRowKey(slice)}
                                slice={slice}
                                index={index}
                                useFormData={{ control, watch, errors, setValue, trigger }}
                                fields={fields}
                                removeRow={removeRow}
                                isPeriodClosed={isUpdate ? Boolean(slice.periodClosed) : false}
                                slicesLength={fields.length}
                                isUpdate={isUpdate || advanceExistsOnTPD}
                                isForfait={isForfait}
                                compact
                                readOnly={readOnly}
                                minPaymentDate={minPaymentDate}
                            />
                        ))}
                    </div>
                ) : (
                    <AdvanceSlicesTable
                        formData={formData}
                        fields={fields}
                        watchedSlices={watchedSlices ?? []}
                        removeRow={removeRow}
                        isUpdate={isUpdate || advanceExistsOnTPD}
                        isForfait={isForfait}
                        readOnly={readOnly}
                        minPaymentDate={minPaymentDate}
                    />
                )}

                <div className="slices-footer">
                    <SmallBody>
                        {totalLabel ?? t(`AdvancePayment.TotalAmount_${type}`)}{" "}
                        {formatNumber(total, { isCurrency: true })}
                    </SmallBody>
                    {!readOnly && (
                        <div className="new-slices-btn">
                            <AddTextButton
                                haveClickedOnButton={false}
                                action={appendRow}
                                btnText={{
                                    clicked: t("AdvancePayment.CreateNewSlice"),
                                    pending: t("AdvancePayment.CreateNewSlice"),
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {type === "package" && (
                <div className="forceOver4Pass">
                    <RadioFormComponent
                        control={control}
                        controlName="forceOver4Pass"
                        label={t("AdvancePayment.ForceOver4Pass")}
                        labelDescription={t("AdvancePayment.ForceOver4PassDesc")}
                        options={[
                            { label: t("ModalBase.No"), defaultChecked: true },
                            { label: t("ModalBase.Yes"), defaultChecked: false },
                        ]}
                    />
                </div>
            )}
        </div>
    );
};

export default AdvanceSlicesFormBody;
